/**
 * CaptionsReviewQueuePageV2 — ports the prototype's
 * `.screen-captions-queue` (cms-redesign-mockup.html ~8884+) onto the
 * existing `/admin/captions/review-queue` data flow.
 *
 * Workflow surface:
 *   • Status SegmentedControl (All / Queued / Processing / Pending /
 *     Reviewed / Edited / Failed). Driven by `statusFilter`; resets
 *     paging on change.
 *   • Bulk-action bar (shown when selection > 0): "N selected",
 *     "Select all M matching" (fires fetchReviewQueueCaptionIds to
 *     pull every caption_id across pages), "Approve N", "Clear".
 *     Only `auto_pending_review` rows count toward the
 *     header-checkbox approve set; the server skips other states
 *     with a reason on the bulk-approve response, which we surface
 *     in the result Swal.
 *   • LibTable: Select / Session / Lang / Status / Source / Updated /
 *     Flags / Actions. Status renders as the shared CapStatus pill;
 *     Lang as LangChip; Source as SourceTag; flags as tinted chips
 *     truncated at four with a "+N" overflow indicator.
 *   • Footer: simple Prev/Next pager + per-page select. The dataset
 *     can be large (10k+ pending reviews), so we keep it backend-
 *     paginated (`reviewQueue.data` is the current page, `total_count`
 *     drives the pager).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  bulkApproveCaptions,
  fetchCaptionsReviewQueue,
  fetchReviewQueueCaptionIds,
} from '../../../actions/captionsActions';
import {
  PageHeader,
  Button,
  SegmentedControl,
  LibTable,
  IconButton,
  cn,
} from '../../redesign';
import {
  CapStatus,
  LangChip,
  SourceTag,
  FlagChip,
} from '../../Captions/v2/captionsPrimitives';

/* ─── Icons ────────────────────────────────────────────────────────── */

const OpenInNewSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const STATUS_OPTIONS = [
  { value: 'all',                 label: 'All' },
  { value: 'queued',              label: 'Queued' },
  { value: 'processing',          label: 'Processing' },
  { value: 'auto_pending_review', label: 'Pending review' },
  { value: 'human_reviewed',      label: 'Reviewed' },
  { value: 'human_edited',        label: 'Edited' },
  { value: 'failed',              label: 'Failed' },
];

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function CaptionsReviewQueuePageV2() {
  const dispatch = useDispatch();
  const reviewQueue = useSelector((s) => s.captions.reviewQueue);
  const isLoading = useSelector((s) => s.captions.isLoading);

  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1); // 1-indexed to match the API.
  const [perPage, setPerPage] = useState(25);
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  // Debounced copy so we don't fire a request on every keystroke. The
  // input updates `search` immediately (responsive field); ~300ms later
  // the trimmed query AND the page reset are applied TOGETHER, so the
  // fetch never runs with a stale (old/empty) search while the page has
  // already jumped to 1 (Bugbot #434). A leading "#" the placeholder
  // invites ("#1608") is stripped so it reaches the API as bare digits —
  // the endpoint only treats all-digit terms as a session-id match.
  // React 18 batches these two setters inside the timeout, so the fetch
  // effect fires once with both new values.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const lastAppliedSearchRef = useRef('');
  useEffect(() => {
    const t = setTimeout(() => {
      const next = search.trim().replace(/^#/, '');
      // Only apply (and reset to page 1) when the EFFECTIVE query actually
      // changes. Trailing spaces or "#1608" vs "1608" normalize to the same
      // term, so they must not re-fetch or bounce the user back to page 1
      // (Bugbot #434). When it does change, the query + page reset are set
      // together (React 18 batches them) so the fetch fires once.
      if (next !== lastAppliedSearchRef.current) {
        lastAppliedSearchRef.current = next;
        setDebouncedSearch(next);
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    dispatch(
      fetchCaptionsReviewQueue({
        status: statusFilter,
        page,
        per_page: perPage,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }),
    );
  }, [dispatch, statusFilter, page, perPage, debouncedSearch]);

  const rows = reviewQueue?.data ?? [];
  const totalCount = reviewQueue?.total_count ?? 0;
  const pageCount = reviewQueue?.page_count ?? 1;
  const hasMore = reviewQueue?.has_more ?? false;

  const approvableRows = useMemo(
    () => rows.filter((r) => r.status === 'auto_pending_review'),
    [rows],
  );
  const approvableSelectedCount = useMemo(
    () =>
      Array.from(selected).filter((id) =>
        approvableRows.some((r) => r.session_caption_id === id),
      ).length,
    [selected, approvableRows],
  );

  const allApprovableOnPageSelected =
    approvableRows.length > 0 &&
    approvableSelectedCount === approvableRows.length;
  const someApprovableOnPageSelected =
    approvableSelectedCount > 0 && !allApprovableOnPageSelected;

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      approvableRows.forEach((r) => {
        if (checked) next.add(r.session_caption_id);
        else next.delete(r.session_caption_id);
      });
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const onSelectAllMatching = async () => {
    setSubmitting(true);
    try {
      const result = await dispatch(fetchReviewQueueCaptionIds(statusFilter, debouncedSearch));
      const ids = result.ids ?? [];
      if (ids.length === 0) {
        Swal.fire({ icon: 'info', title: 'Nothing to select', text: 'No matching rows.', timer: 1200 });
        return;
      }
      setSelected((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
      if (result.totalCount > result.requestedLimit) {
        Swal.fire({
          icon: 'warning',
          title: `Selected first ${ids.length} rows`,
          text: `${result.totalCount} match the filter — only the first ${result.requestedLimit} were added to the selection. Approve these, then click again.`,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onBulkApprove = async () => {
    if (selected.size === 0) return;
    const captionIds = Array.from(selected);
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: `Approve ${captionIds.length} caption${captionIds.length === 1 ? '' : 's'} as-is?`,
      text: "Only rows currently in 'auto_pending_review' will be approved. Others (already-reviewed, processing, failed) are skipped.",
      showCancelButton: true,
      confirmButtonText: 'Approve',
    });
    if (!isConfirmed) return;
    setSubmitting(true);
    try {
      const result = await dispatch(bulkApproveCaptions(captionIds));
      clearSelection();
      dispatch(
        fetchCaptionsReviewQueue({
          status: statusFilter,
          page,
          per_page: perPage,
          // Keep the active search on the post-approve refetch, else the
          // table repopulates unfiltered while the search box still shows
          // the term (Bugbot PR #434).
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        }),
      );
      Swal.fire({
        icon: (result?.skipped ?? 0) === 0 ? 'success' : 'warning',
        title: `Approved ${result?.approved ?? 0}`,
        text:
          (result?.skipped ?? 0) === 0
            ? 'Each approved caption is now servable to players.'
            : `${result.skipped} skipped — they weren't in 'auto_pending_review' anymore (already approved, processing, or failed since you ticked them).`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const totalLabel =
    statusFilter === 'all'
      ? `${totalCount.toLocaleString()} in queue`
      : `${totalCount.toLocaleString()} ${(STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? statusFilter).toLowerCase()}`;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Captions"
        title="Review Queue"
        total={totalLabel}
      />

      <div
        className="flex items-center"
        style={{ padding: '0 28px 16px', gap: 12, flexWrap: 'wrap' }}
      >
        <SegmentedControl
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
          options={STATUS_OPTIONS}
        />

        {/* Search — by session name (substring) or #id. The page reset is
         *  applied together with the debounced query (see the debounce
         *  effect), so the fetch never runs with a stale search. */}
        <div className="relative" style={{ marginLeft: 'auto', minWidth: 240 }}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by session name or #ID…"
            aria-label="Search the review queue by session name or ID"
            className="bg-surface border border-sand text-navy w-full placeholder:text-text-muted"
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
        </div>
      </div>

      {/* Bulk-action bar — appears only when selection > 0. Coral
       *  background gives the bar a clear "you have a pending action"
       *  signal. Matches the prototype's `.bulk-bar` placement. */}
      {selected.size > 0 && (
        <div
          className="flex items-center bg-navy text-bg"
          style={{
            margin: '0 28px 16px',
            padding: '10px 16px',
            borderRadius: 8,
            gap: 12,
            flexWrap: 'wrap',
            boxShadow: '0 2px 6px #2C3E5040',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            <strong>{selected.size}</strong> selected
          </span>
          <span className="font-mono" style={{ fontSize: 11.5, opacity: 0.75 }}>
            Approve to mark as <code style={{ color: '#FFFFFFC0' }}>human_reviewed</code>
          </span>
          <div
            className="flex items-center"
            style={{ marginLeft: 'auto', gap: 8 }}
          >
            <Button variant="ghost" onClick={clearSelection} className="text-white hover:bg-white/10">
              Clear
            </Button>
            <Button
              variant="default"
              onClick={onSelectAllMatching}
              disabled={submitting || totalCount === 0}
            >
              Select all {totalCount} matching
            </Button>
            <Button variant="primary" onClick={onBulkApprove} disabled={submitting}>
              Approve {selected.size}
            </Button>
          </div>
        </div>
      )}

      {isLoading && rows.length === 0 ? (
        <div className="flex items-center justify-center text-text-muted" style={{ height: 256 }}>
          Loading review queue…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          No captions match the current filter.
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            {/* Column widths — comments inlined *above* each col to avoid
             *  the validateDOMNesting warning. JSX strips inter-line
             *  whitespace, but a literal space between `<col />` and a
             *  trailing `{/* … *‍/}` on the same line is preserved as a
             *  text node — and <colgroup> doesn't allow text children. */}
            <colgroup>
              {/* checkbox */}
              <col style={{ width: 40 }} />
              {/* session (name + #id) */}
              <col style={{ width: '22%' }} />
              {/* lang */}
              <col style={{ width: 60 }} />
              {/* status */}
              <col style={{ width: '14%' }} />
              {/* source */}
              <col style={{ width: '10%' }} />
              {/* generated */}
              <col style={{ width: '13%' }} />
              {/* flags */}
              <col />
              {/* actions — needs ~110px so the leading-icon 'Review'
               *  button fits without cropping. LibTable.Td gives the
               *  last cell 12px left + 28px right padding (pr-7), so
               *  content area = 110 - 40 = 70px = sm Button width. */}
              <col style={{ width: 110 }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th aria-label="Select" style={{ paddingLeft: 16 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all pending on this page"
                    checked={allApprovableOnPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someApprovableOnPageSelected;
                    }}
                    onChange={(e) => toggleAllOnPage(e.target.checked)}
                  />
                </LibTable.Th>
                <LibTable.Th>Session</LibTable.Th>
                <LibTable.Th>Lang</LibTable.Th>
                <LibTable.Th>Status</LibTable.Th>
                <LibTable.Th>Source</LibTable.Th>
                <LibTable.Th>Updated</LibTable.Th>
                <LibTable.Th>Flags</LibTable.Th>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {rows.map((row) => {
                const checkable = row.status === 'auto_pending_review';
                const isSelected = selected.has(row.session_caption_id);
                return (
                  <LibTable.Row key={row.session_caption_id}>
                    <td
                      className="border-b border-line"
                      style={{ paddingLeft: 16, paddingTop: 14, paddingBottom: 14, paddingRight: 8 }}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select caption ${row.session_caption_id}`}
                        disabled={!checkable}
                        checked={isSelected}
                        onChange={() => toggleRow(row.session_caption_id)}
                        title={
                          checkable
                            ? ''
                            : 'Only auto_pending_review rows can be bulk-approved.'
                        }
                      />
                    </td>
                    <LibTable.Td>
                      {/* Session identity — the API now returns
                       *  `session: { session_id, session_name, ... }`
                       *  inline on each row (see APIv2 PR #577). Show
                       *  the name as the primary line so operators
                       *  recognize what they're reviewing without
                       *  bouncing to the Sessions list; the #id stays
                       *  visible as a mono sub-line for cross-
                       *  reference. Falls back to just the #id when
                       *  the API hasn't shipped the new field yet. */}
                      <Link
                        to={`/captions/edit/${row.session_id}/${row.language_code}?source=${row.source}`}
                        className="block no-underline hover:text-teal-green-dark"
                        style={{ color: 'inherit' }}
                      >
                        {row.session?.session_name ? (
                          <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
                            <span
                              className={cn(
                                'font-semibold truncate',
                                row.session.session_is_active === false
                                  ? 'text-text-light italic'
                                  : 'text-navy',
                              )}
                              style={{ fontSize: 13.5 }}
                              title={row.session.session_name}
                            >
                              {row.session.session_name}
                            </span>
                            <span
                              className="font-mono text-text-muted"
                              style={{ fontSize: 11.5 }}
                            >
                              #{row.session_id}
                            </span>
                          </div>
                        ) : (
                          <span
                            className="text-navy font-semibold"
                            style={{ fontSize: 13.5 }}
                          >
                            #{row.session_id}
                          </span>
                        )}
                      </Link>
                    </LibTable.Td>
                    <LibTable.Td>
                      <LangChip code={row.language_code} status={row.status} />
                    </LibTable.Td>
                    <LibTable.Td>
                      <CapStatus
                        status={row.status}
                        flagCount={(row.flags?.length ?? 0) > 0 ? row.flags.length : 0}
                      />
                    </LibTable.Td>
                    <LibTable.Td>
                      <SourceTag source={row.source} />
                    </LibTable.Td>
                    <LibTable.Td>
                      {/* Last activity on the row — covers re-cue, inline
                          edit, upload and approve, not just the original
                          transcription. The tooltip keeps the per-event
                          breakdown so "generated when?" stays one hover
                          away. */}
                      <span
                        className="text-text-muted font-mono"
                        style={{ fontSize: 11.5 }}
                        title={[
                          row.generated_at
                            ? `Generated ${new Date(row.generated_at).toLocaleString()}`
                            : null,
                          row.recued_at
                            ? `Re-cued ${new Date(row.recued_at).toLocaleString()}`
                            : null,
                          row.reviewed_at
                            ? `Reviewed ${new Date(row.reviewed_at).toLocaleString()}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join('\n') || undefined}
                      >
                        {row.updated_at
                          ? new Date(row.updated_at).toLocaleString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </span>
                    </LibTable.Td>
                    <LibTable.Td>
                      <span className="inline-flex flex-wrap" style={{ gap: 4 }}>
                        {(row.flags ?? []).slice(0, 4).map((flag) => (
                          <FlagChip key={flag.id} flag={flag} />
                        ))}
                        {(row.flags?.length ?? 0) > 4 && (
                          <span
                            className="inline-flex items-center font-mono text-text-muted bg-cream border border-sand"
                            style={{
                              fontSize: 10.5,
                              padding: '1px 6px',
                              borderRadius: 4,
                            }}
                          >
                            +{row.flags.length - 4}
                          </span>
                        )}
                        {(!row.flags || row.flags.length === 0) && (
                          <span className="text-text-muted italic" style={{ fontSize: 11 }}>—</span>
                        )}
                      </span>
                    </LibTable.Td>
                    <LibTable.Td style={{ textAlign: 'right' }}>
                      <Link
                        to={`/captions/edit/${row.session_id}/${row.language_code}?source=${row.source}`}
                        className="no-underline"
                      >
                        <Button variant="default" size="sm" leadingIcon={OpenInNewSvg}>
                          Review
                        </Button>
                      </Link>
                    </LibTable.Td>
                  </LibTable.Row>
                );
              })}
            </tbody>
          </LibTable>

          {/* Pager + per-page select — modest UI, sits beneath the
           *  table inside the same gutter. */}
          <div
            className="flex items-center justify-between"
            style={{ padding: '16px 4px 0', gap: 12, flexWrap: 'wrap' }}
          >
            <div
              className="text-text-muted font-mono"
              style={{ fontSize: 12 }}
            >
              Page <strong className="text-navy">{page}</strong> of{' '}
              <strong className="text-navy">{pageCount}</strong>
              {' · '}
              {totalCount.toLocaleString()} total
            </div>
            <div className="flex items-center" style={{ gap: 8 }}>
              <label
                className="text-text-muted font-mono"
                style={{ fontSize: 12 }}
              >
                Per page
              </label>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                className="bg-surface border border-sand text-navy"
                style={{
                  padding: '4px 8px',
                  borderRadius: 6,
                  fontSize: 12.5,
                  fontFamily: 'inherit',
                }}
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <Button
                variant="default"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹ Prev
              </Button>
              <Button
                variant="default"
                size="sm"
                disabled={!hasMore}
                onClick={() => setPage((p) => p + 1)}
              >
                Next ›
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
